import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControlLabel,
	Grid,
	List,
	ListItem,
	SelectChangeEvent,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
	createCertificates,
	downloadReport,
	generateStage3WorkCodes,
	getStage3CodePrintPresets,
	getOlympicsSettings,
	getThirdAStageStudents,
	getSurveyStats,
	recalculateSecondBQualification,
	restoreSchoolDistrictsFromPreviousEdition,
	saveStage3CodePrintPreset,
	setStage3CodePrintDefaultPreset,
	updateOlympicsSettings,
} from '../../api/content-api';
import {
	getStudentOpinionDriveSettings,
	saveStudentOpinionDriveSettings,
	getPanelHelpSettings,
	savePanelHelpSettings,
	PanelHelpLink,
} from '../../api/user-api';
import SnackbarContext from '../../store/snackbar-context';
import Loader from '../../ui/Loader';
import AuthContext from '../../store/auth-context';
import { useNavigate } from 'react-router-dom';
import { loginPage, panelPage } from '../../store/fixed-routing';
import Olympics from '../../model/Olympics';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/pl';
import SaveIcon from '@mui/icons-material/Save';
import ReplayIcon from '@mui/icons-material/Replay';
import ConfirmationDialog from './ConfirmationDialog';
import OlympicsContext from '../../store/olympics-context';
import NewOlympicsDialog from './NewOlympicsDialog';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useCustomTheme } from '../../store/theme-context';
import { cleanUpHtml } from '../../utils/string-tools';
import TopicsEditorDialog from './TopicsEditorDialog';
import DeclarationsEditorDialog from './DeclarationsEditorDialog';
import District from '../../model/District';
import Stage3CodeActionsBar from './Stage3CodeActionsBar';
import Stage3CodesDialog from './Stage3CodesDialog';
import Stage3AttendanceDialog from './Stage3AttendanceDialog';
import Stage3JuryPairsDialog from './Stage3JuryPairsDialog';
import Stage3DocumentAccessDialog from './Stage3DocumentAccessDialog';
import SchoolsRspoRefreshDialog from './SchoolsRspoRefreshDialog';
import { isSmallOlympicVariant } from '../../config/variant-profile';

type SettingsRowProps = {
	firstChild: any;
	secondChild?: any;
	thirdChild?: any;
};
const SettingsRow = ({ firstChild, secondChild, thirdChild }: SettingsRowProps) => {
	return (
		<Grid
			item
			container
			gap={'20px'}
			flexWrap={'nowrap'}
			mt={2}
		>
			<Grid
				item
				container
			>
				{firstChild}
			</Grid>
			{secondChild && (
				<Grid
					item
					container
				>
					{secondChild}
				</Grid>
			)}
			{thirdChild && (
				<Grid
					item
					container
				>
					{thirdChild}
				</Grid>
			)}
		</Grid>
	);
};

type Stage3CodePrintPreset = {
	slug: string;
	label: string;
	config: Record<string, any>;
};

const DEFAULT_CODES_PRESET_CONFIG: Record<string, any> = {
	labels_per_row: 4,
	rows_per_page: 10,
	col_widths: { first: 24.44, middle: 24.44, last: 24.44 },
	row_heights: { first: 81, middle: 81, last: 81 },
	margins: { left: '0mm', right: '0mm', top: '0mm', bottom: '0mm', header: '0mm', footer: '0mm' },
	fit: { mode: 'columns', width: 1, height: 0, zoom: 100 },
};

const DEFAULT_ATTENDANCE_LAYOUT_CONFIG = {
	rowsPerPage: 30,
	rowHeight: 30,
	separatorRowHeight: 0,
};

const getExcelColumnLetter = (index: number): string => {
	let i = Math.max(0, index);
	let letters = '';
	do {
		letters = String.fromCharCode((i % 26) + 65) + letters;
		i = Math.floor(i / 26) - 1;
	} while (i >= 0);
	return letters;
};

const normalizeCodesPresetDraftConfig = (raw?: Record<string, any>): Record<string, any> => {
	const cfg = raw || {};
	const labelsPerRow = Math.max(1, Number(cfg?.labels_per_row ?? DEFAULT_CODES_PRESET_CONFIG.labels_per_row));
	const rowsPerPage = Math.max(1, Number(cfg?.rows_per_page ?? DEFAULT_CODES_PRESET_CONFIG.rows_per_page));
	const sourceColWidths = cfg?.col_widths || {};

	const firstByName = sourceColWidths?.first;
	const middleByName = sourceColWidths?.middle;
	const lastByName = sourceColWidths?.last;
	const firstByLegacy = sourceColWidths?.A;
	const lastByLegacy = sourceColWidths?.[getExcelColumnLetter(labelsPerRow - 1)];

	let middleFromLegacy: number | undefined;
	if (labelsPerRow > 2) {
		for (let idx = 1; idx <= labelsPerRow - 2; idx++) {
			const value = sourceColWidths?.[getExcelColumnLetter(idx)];
			if (value !== undefined && value !== null) {
				middleFromLegacy = Number(value);
				break;
			}
		}
	}

	const first = Number(firstByName ?? firstByLegacy ?? 24.44);
	const middle = Number(middleByName ?? middleFromLegacy ?? first ?? 24.44);
	const last = Number(lastByName ?? lastByLegacy ?? middle ?? first ?? 24.44);

	return {
		...DEFAULT_CODES_PRESET_CONFIG,
		...cfg,
		labels_per_row: labelsPerRow,
		rows_per_page: rowsPerPage,
		col_widths: { first, middle, last },
		row_heights: {
			first: Number(cfg?.row_heights?.first ?? DEFAULT_CODES_PRESET_CONFIG.row_heights.first),
			middle: Number(cfg?.row_heights?.middle ?? DEFAULT_CODES_PRESET_CONFIG.row_heights.middle),
			last: Number(cfg?.row_heights?.last ?? DEFAULT_CODES_PRESET_CONFIG.row_heights.last),
		},
		margins: {
			left: String(cfg?.margins?.left ?? DEFAULT_CODES_PRESET_CONFIG.margins.left),
			right: String(cfg?.margins?.right ?? DEFAULT_CODES_PRESET_CONFIG.margins.right),
			top: String(cfg?.margins?.top ?? DEFAULT_CODES_PRESET_CONFIG.margins.top),
			bottom: String(cfg?.margins?.bottom ?? DEFAULT_CODES_PRESET_CONFIG.margins.bottom),
			header: String(cfg?.margins?.header ?? DEFAULT_CODES_PRESET_CONFIG.margins.header),
			footer: String(cfg?.margins?.footer ?? DEFAULT_CODES_PRESET_CONFIG.margins.footer),
		},
		fit: {
			mode: String(cfg?.fit?.mode ?? DEFAULT_CODES_PRESET_CONFIG.fit.mode),
			width: Number(cfg?.fit?.width ?? DEFAULT_CODES_PRESET_CONFIG.fit.width),
			height: Number(cfg?.fit?.height ?? DEFAULT_CODES_PRESET_CONFIG.fit.height),
			zoom: Number(cfg?.fit?.zoom ?? DEFAULT_CODES_PRESET_CONFIG.fit.zoom),
		},
	};
};

const areEqualObjects = (a: Record<string, any>, b: Record<string, any>): boolean =>
	JSON.stringify(a) === JSON.stringify(b);

const SettingsPage = () => {
	const navigate = useNavigate();
	const { palette } = useTheme();
	const { isHighContrast } = useCustomTheme();
	const isSmallOlympic = isSmallOlympicVariant();
	const { setMsg } = useContext(SnackbarContext);
	const { logout, token, isAdmin } = useContext(AuthContext);
	const { olympics, setOlympics, refresh, refreshTables, districts } = useContext(OlympicsContext);
	const [openNewOlympicsDialog, setOpenNewOlympicsDialog] = useState(false);
	const [openRspoRefreshDialog, setOpenRspoRefreshDialog] = useState(false);
	const [dialogContent, setDialogContent] = useState<string | null>(null);
	const [sendingRequest, setSendingRequest] = useState(false);
	const [okFunction, setOkFunction] = useState<Function | null>(null);
	const [publishEditEnabled, setPublishEditEnabled] = useState(false);
	const [isSurveyPreviewOpen, setIsSurveyPreviewOpen] = useState(false);
	const [surveyPreviewTouched, setSurveyPreviewTouched] = useState(false);
	const [surveyPreviewLoaded, setSurveyPreviewLoaded] = useState(false);
	const [surveyTopics, setSurveyTopics] = useState<any[] | null>(null);
	const [surveyDeclarations, setSurveyDeclarations] = useState<any[] | null>(null);
	const [isSurveyPreviewLoading, setIsSurveyPreviewLoading] = useState(false);
	const [isOralTopicsDialogOpen, setIsOralTopicsDialogOpen] = useState(false);
	const [isLinguisticTopicsDialogOpen, setIsLinguisticTopicsDialogOpen] = useState(false);
	const [surveySpecializations, setSurveySpecializations] = useState<any[] | null>(null);
	const [activeOralSpecialization, setActiveOralSpecialization] = useState<any | null>(null);
	const [pendingOralDialogOpen, setPendingOralDialogOpen] = useState(false);
	const [isDeclarationsDialogOpen, setIsDeclarationsDialogOpen] = useState(false);
	const [isSurveyStatsOpen, setIsSurveyStatsOpen] = useState(false);
	const [surveyStatsTouched, setSurveyStatsTouched] = useState(false);
	const [surveyStatsLoading, setSurveyStatsLoading] = useState(false);
	const [surveyStats, setSurveyStats] = useState<any | null>(null);
	const [isCodesDownloading, setIsCodesDownloading] = useState(false);
	const [isCodesDialogOpen, setIsCodesDialogOpen] = useState(false);
	const [selectedCodesDistrict, setSelectedCodesDistrict] = useState<string>('all');
	const [selectedCodesPrintProfile, setSelectedCodesPrintProfile] = useState<string>('default');
	const [codesPresets, setCodesPresets] = useState<Stage3CodePrintPreset[]>([]);
	const [codesDefaultPresetSlug, setCodesDefaultPresetSlug] = useState<string>('default');
	const [isCodesPresetsLoading, setIsCodesPresetsLoading] = useState(false);
	const [isCodesPresetSaving, setIsCodesPresetSaving] = useState(false);
	const [codesPresetSlugInput, setCodesPresetSlugInput] = useState<string>('default');
	const [codesPresetLabelInput, setCodesPresetLabelInput] = useState<string>('Standard');
	const [codesPresetDraftConfig, setCodesPresetDraftConfig] =
		useState<Record<string, any>>(DEFAULT_CODES_PRESET_CONFIG);
	const [codesPresetSavedSnapshot, setCodesPresetSavedSnapshot] = useState<{
		slug: string;
		label: string;
		config: Record<string, any>;
	} | null>(null);
	const [codesSetAsDefaultOnSave, setCodesSetAsDefaultOnSave] = useState<boolean>(false);
	const [isCodesGridTestEnabled, setIsCodesGridTestEnabled] = useState<boolean>(() => {
		if (typeof window === 'undefined') {
			return false;
		}
		return window.sessionStorage.getItem('stage3_codes_grid_test_enabled') === '1';
	});
	const [isAttendanceDownloading, setIsAttendanceDownloading] = useState(false);
	const [isAttendanceDialogOpen, setIsAttendanceDialogOpen] = useState(false);
	const [isJuryPairsDialogOpen, setIsJuryPairsDialogOpen] = useState(false);
	const [isDocumentAccessDialogOpen, setIsDocumentAccessDialogOpen] = useState(false);
	const [selectedAttendanceDistrict, setSelectedAttendanceDistrict] = useState<string>('all');
	const [attendanceLayoutConfig, setAttendanceLayoutConfig] = useState<{
		rowsPerPage: number;
		rowHeight: number;
		separatorRowHeight: number;
	}>(() => {
		if (typeof window === 'undefined') {
			return DEFAULT_ATTENDANCE_LAYOUT_CONFIG;
		}
		try {
			const raw = window.localStorage.getItem('stage3_attendance_layout_config');
			if (!raw) {
				return DEFAULT_ATTENDANCE_LAYOUT_CONFIG;
			}
			const parsed = JSON.parse(raw);
			return {
				rowsPerPage: Number(parsed?.rowsPerPage ?? DEFAULT_ATTENDANCE_LAYOUT_CONFIG.rowsPerPage),
				rowHeight: Number(parsed?.rowHeight ?? DEFAULT_ATTENDANCE_LAYOUT_CONFIG.rowHeight),
				separatorRowHeight: Number(parsed?.separatorRowHeight ?? DEFAULT_ATTENDANCE_LAYOUT_CONFIG.separatorRowHeight),
			};
		} catch (_error) {
			return DEFAULT_ATTENDANCE_LAYOUT_CONFIG;
		}
	});
	const [isCodesGenerating, setIsCodesGenerating] = useState(false);
	const [eligibleCodeDistricts, setEligibleCodeDistricts] = useState<District[]>([]);
	const [opinionDriveFolder, setOpinionDriveFolder] = useState('');
	const [opinionDriveEnvLocked, setOpinionDriveEnvLocked] = useState(false);
	const [opinionDriveTokenOk, setOpinionDriveTokenOk] = useState(false);
	const [opinionDriveSaving, setOpinionDriveSaving] = useState(false);
	const [helpIntro, setHelpIntro] = useState('');
	const [helpLinks, setHelpLinks] = useState<PanelHelpLink[]>([]);
	const [helpSaving, setHelpSaving] = useState(false);

	useEffect(() => {
		if (!isAdmin) {
			navigate(panelPage);
		} else {
			// Refresh settings when entering settings page to get latest data
			refresh();
		}
	}, []);

	useEffect(() => {
		if (!token || !isAdmin) {
			return;
		}
		getStudentOpinionDriveSettings(token)
			.then((res) => {
				setOpinionDriveFolder(res.data.folderUrl || '');
				setOpinionDriveEnvLocked(!!res.data.envOverride);
				setOpinionDriveTokenOk(!!res.data.driveTokenConfigured);
			})
			.catch(() => {});
		getPanelHelpSettings(token)
			.then((res) => {
				setHelpIntro(res.data.helpIntro || '');
				setHelpLinks(Array.isArray(res.data.helpLinks) ? res.data.helpLinks : []);
			})
			.catch(() => {});
	}, [token, isAdmin]);

	const shouldAutoOpenSurveyPreview = Boolean(
		olympics?.secondBStagePublished && !olympics?.secondBStageQualificationPublished,
	);

	useEffect(() => {
		if (!surveyPreviewTouched && olympics) {
			setIsSurveyPreviewOpen(shouldAutoOpenSurveyPreview);
		}
	}, [olympics, shouldAutoOpenSurveyPreview, surveyPreviewTouched]);

	const loadSurveyPreview = useCallback(() => {
		if (surveyPreviewLoaded || isSurveyPreviewLoading) {
			return;
		}
		setIsSurveyPreviewLoading(true);
		getOlympicsSettings(token)
			.then((response) => {
				if (response.status === 200) {
					setSurveyTopics(response.data?.topics || []);
					setSurveyDeclarations(response.data?.declarations || []);
					setSurveySpecializations(response.data?.specializations || []);
					setSurveyPreviewLoaded(true);
				} else {
					setMsg({ msg: response.data });
				}
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response?.status === 401 || exception.response?.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				}
			})
			.finally(() => {
				setIsSurveyPreviewLoading(false);
			});
	}, [isSurveyPreviewLoading, logout, navigate, setMsg, surveyPreviewLoaded, token]);

	useEffect(() => {
		if (isSurveyPreviewOpen) {
			loadSurveyPreview();
		}
	}, [isSurveyPreviewOpen, loadSurveyPreview]);

	const shouldAutoOpenSurveyStats = Boolean(
		olympics?.secondBStageQualificationPublished && !olympics?.thirdBStagePublished,
	);

	useEffect(() => {
		if (!surveyStatsTouched && olympics) {
			setIsSurveyStatsOpen(shouldAutoOpenSurveyStats);
		}
	}, [olympics, shouldAutoOpenSurveyStats, surveyStatsTouched]);

	const sortedSurveyTopics = (surveyTopics || []).slice().sort((a: any, b: any) => {
		const orderDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0);
		if (orderDiff !== 0) {
			return orderDiff;
		}
		return (a.id ?? 0) - (b.id ?? 0);
	});
	const sortedSurveySpecializations = (surveySpecializations || [])
		.slice()
		.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));

	useEffect(() => {
		if (!pendingOralDialogOpen) {
			return;
		}
		if (!surveyPreviewLoaded) {
			return;
		}
		if (sortedSurveySpecializations.length > 0) {
			setActiveOralSpecialization(sortedSurveySpecializations[0]);
			setIsOralTopicsDialogOpen(true);
			setPendingOralDialogOpen(false);
		} else {
			setPendingOralDialogOpen(false);
			setMsg({ msg: 'Brak specjalizacji do edycji tematów' });
		}
	}, [pendingOralDialogOpen, surveyPreviewLoaded, sortedSurveySpecializations, setMsg]);

	const handleSurveyPreviewChange = (_event: React.SyntheticEvent, expanded: boolean) => {
		setSurveyPreviewTouched(true);
		setIsSurveyPreviewOpen(expanded);
	};

	const handleTopicsUpdated = (updated: any[], type: 'oral' | 'linguistic') => {
		setSurveyTopics((prev) => {
			const prevList = prev || [];
			const keep = prevList.filter((topic: any) => {
				const isLinguistic = topic.is_linguistic == 1;
				if (type === 'linguistic') {
					return !isLinguistic;
				}
				if (!activeOralSpecialization) {
					return !isLinguistic;
				}
				return (
					isLinguistic || !(topic.specializations || []).some((spec: any) => spec.id === activeOralSpecialization.id)
				);
			});
			return [...keep, ...updated];
		});
	};

	const handleDeclarationsUpdated = (updated: any[]) => {
		setSurveyDeclarations(updated);
	};

	const openTopicsDialog = (dialogType: 'oral' | 'linguistic') => {
		loadSurveyPreview();
		if (dialogType === 'oral') {
			if (!surveyPreviewLoaded) {
				setPendingOralDialogOpen(true);
				return;
			}
			if (activeOralSpecialization) {
				setIsOralTopicsDialogOpen(true);
			} else if (sortedSurveySpecializations.length > 0) {
				setActiveOralSpecialization(sortedSurveySpecializations[0]);
				setIsOralTopicsDialogOpen(true);
			} else {
				setMsg({ msg: 'Brak specjalizacji do edycji tematów' });
			}
		} else {
			setIsLinguisticTopicsDialogOpen(true);
		}
	};

	const openDeclarationsDialog = () => {
		loadSurveyPreview();
		setIsDeclarationsDialogOpen(true);
	};

	const loadSurveyStats = useCallback(() => {
		if (surveyStatsLoading || surveyStats) {
			return;
		}
		setSurveyStatsLoading(true);
		getSurveyStats(token)
			.then((response) => {
				if (response.status === 200) {
					setSurveyStats(response.data);
				} else {
					setMsg({ msg: response.data });
				}
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response?.status === 401 || exception.response?.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				}
			})
			.finally(() => {
				setSurveyStatsLoading(false);
			});
	}, [logout, navigate, setMsg, surveyStats, surveyStatsLoading, token]);

	useEffect(() => {
		if (isSurveyStatsOpen) {
			loadSurveyStats();
		}
	}, [isSurveyStatsOpen, loadSurveyStats]);

	const handleSurveyStatsChange = (_event: React.SyntheticEvent, expanded: boolean) => {
		setSurveyStatsTouched(true);
		setIsSurveyStatsOpen(expanded);
	};

	const setProperty = (obj: any, path: string, value: any): any => {
		const [head, ...rest] = path.split('.');

		return {
			...obj,
			[head]: rest.length ? setProperty(obj[head], rest.join('.'), value) : value,
		};
	};
	const handlePointsChange = (event: any, path: string) => {
		const value = Number(event.target.value);
		if (olympics) {
			setOlympics(setProperty(olympics, path, value));
		}
	};

	const handleValueChange = (path: string, value: any) => {
		if (olympics) {
			setOlympics(setProperty(olympics, path, value));
		}
	};

	const handleSettings = (path: string, value: any) => {
		if (olympics) {
			setOlympics(setProperty(olympics, path, value.target.value));
		}
	};

	const isMaxInvalid = (maxValue?: number, minValue?: number) => {
		if (!maxValue) {
			return false;
		}
		if (!minValue) {
			return false;
		}
		return maxValue <= minValue;
	};

	const maxHelperText = (maxValue?: number, minValue?: number) => {
		if (isMaxInvalid(maxValue, minValue)) {
			return 'Maksimum musi być większe od limitu kwalifikacji';
		}
		return '';
	};

	const closeDialog = () => {
		setSendingRequest(false);
		setOkFunction(null);
		setDialogContent(null);
	};

	const updateOlympics = () => {
		if (olympics) {
			const validationErrors: string[] = [];
			const pushError = (label: string, maxValue: number, minValue: number) => {
				if (maxValue > 0 && minValue > 0 && maxValue <= minValue) {
					validationErrors.push(`${label}: maksimum musi być większe od limitu kwalifikacji`);
				}
			};

			pushError(
				'Etap IIA - część pisemna nr 1 (wariant 1)',
				olympics.secondStagePoints.partA.max.v1.part1,
				olympics.secondStagePoints.partA.v1.part1,
			);
			pushError(
				'Etap IIA - część pisemna nr 2 (wariant 1)',
				olympics.secondStagePoints.partA.max.v1.part2,
				olympics.secondStagePoints.partA.v1.part2,
			);
			pushError(
				'Etap IIA - część pisemna nr 1 (wariant 2)',
				olympics.secondStagePoints.partA.max.v2.part1,
				olympics.secondStagePoints.partA.v2.part1,
			);
			pushError(
				'Etap IIA - część pisemna nr 2 (wariant 2)',
				olympics.secondStagePoints.partA.max.v2.part2,
				olympics.secondStagePoints.partA.v2.part2,
			);
			if (!isSmallOlympic) {
				pushError(
					'Etap IIB - część ustna nr 1',
					olympics.secondStagePoints.partB.max.part1,
					olympics.secondStagePoints.partB.part1,
				);
				pushError(
					'Etap IIB - część ustna nr 2',
					olympics.secondStagePoints.partB.max.part2,
					olympics.secondStagePoints.partB.part2,
				);
			}
			pushError(
				isSmallOlympic ? 'Etap IIB - punkty' : 'Etap IIB - część ustna nr 3',
				olympics.secondStagePoints.partB.max.part3,
				olympics.secondStagePoints.partB.part3,
			);
			pushError(
				'Etap IIIA - część pisemna',
				olympics.thirdStagePoints.partA.maxPoints,
				olympics.thirdStagePoints.partA.points,
			);

			if (validationErrors.length) {
				setMsg({ msg: validationErrors.join(' | '), severity: 'warning' });
				return;
			}
			setSendingRequest(true);
			updateOlympicsSettings(token, olympics)
				.then((response) => {
					if (response.status === 200) {
						setOlympics(Olympics.fromApiResponse(response.data));
						setMsg({ msg: 'Ustawienia zostały zaktualizowane', severity: 'success' });
					} else {
						setMsg({ msg: response.data });
					}
					closeDialog();
				})
				.catch((exception) => {
					console.error(exception);
					if (exception.response.status === 401 || exception.response.status === 403) {
						logout();
						navigate(loginPage);
						setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
					} else {
						setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
					}
					closeDialog();
				});
		} else {
			closeDialog();
		}
	};

	const generateCertificates = (type: string) => {
		if (type) {
			setSendingRequest(true);
			createCertificates(token, { type })
				.then((response) => {
					if (response.status === 200) {
						setMsg({ msg: response.data.message, severity: 'success' });
					} else {
						setMsg({ msg: response.data });
					}
					closeDialog();
				})
				.catch((exception) => {
					console.error(exception);
					if (exception.response.status === 401 || exception.response.status === 403) {
						logout();
						navigate(loginPage);
						setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
					} else {
						setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
					}
					closeDialog();
				});
		} else {
			closeDialog();
		}
	};

	const isRecalculateDisabled = Boolean(
		olympics?.secondBStageQualificationPublished || !olympics?.secondAStagePublished,
	);

	const publishTooltip = (text: string) =>
		publishEditEnabled ? text : 'Kliknij Edytuj, aby odblokować zmianę publikacji';

	const recalculateQualification = () => {
		setSendingRequest(true);
		recalculateSecondBQualification(token)
			.then((response) => {
				if (response.status === 200) {
					setMsg({ msg: 'Przeliczono kwalifikację do etapu III', severity: 'success' });
				} else {
					setMsg({ msg: response.data });
				}
				closeDialog();
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				}
				closeDialog();
			});
	};

	const buildCodesPresetPayloadConfig = (): Record<string, any> => {
		const cfg = normalizeCodesPresetDraftConfig(codesPresetDraftConfig);
		return {
			...cfg,
			col_widths: {
				first: Number(cfg?.col_widths?.first ?? 24.44),
				middle: Number(cfg?.col_widths?.middle ?? 24.44),
				last: Number(cfg?.col_widths?.last ?? 24.44),
			},
		};
	};

	const hasCodesPresetUnsavedChanges = Boolean(
		codesPresetSavedSnapshot &&
			(codesPresetSlugInput !== codesPresetSavedSnapshot.slug ||
				codesPresetLabelInput !== codesPresetSavedSnapshot.label ||
				!areEqualObjects(
					normalizeCodesPresetDraftConfig(codesPresetDraftConfig),
					normalizeCodesPresetDraftConfig(codesPresetSavedSnapshot.config),
				)),
	);
	const codesUnsavedHighlightSx = hasCodesPresetUnsavedChanges
		? {
				'& .MuiInputLabel-root': { color: `${palette.primary.main} !important` },
				'& .MuiInputBase-input': { color: `${palette.primary.main} !important` },
				'& .MuiSelect-select': { color: `${palette.primary.main} !important` },
				'& .MuiOutlinedInput-notchedOutline': { borderColor: `${palette.primary.main} !important` },
			}
		: {};

	const downloadStage3Codes = (districtId: string, printProfile: string, printProfileConfig?: Record<string, any>) => {
		setIsCodesDownloading(true);
		const params: Record<string, string | number> = { district_id: districtId, print_profile: printProfile };
		if (printProfileConfig) {
			const payload = { ...printProfileConfig, print_grid_lines: isCodesGridTestEnabled };
			params.print_profile_config = JSON.stringify(payload);
		}
		if (isCodesGridTestEnabled) {
			params.print_grid_test = '1';
		}
		downloadReport(token, 'stage3-codes', params)
			.then((response) => {
				const disposition = response.headers?.['content-disposition'] as string | undefined;
				const match = disposition ? disposition.match(/filename="?([^"]+)"?/i) : null;
				const fileName = match?.[1] || 'Etykiety_kodow_Etap-III.xlsx';

				const url = window.URL.createObjectURL(response.data);
				const a = document.createElement('a');
				a.style.display = 'none';
				a.setAttribute('download', fileName);
				a.href = url;
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				window.URL.revokeObjectURL(url);
				setMsg({ msg: 'Pobrano etykiety kodów', severity: 'success' });
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response?.status === 401 || exception.response?.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					const msg = exception?.response?.data?.message
						? exception?.response?.data?.message
						: 'Błąd serwera. Spróbuj za chwilę';
					setMsg({ msg });
				}
			})
			.finally(() => {
				setIsCodesDownloading(false);
			});
	};

	const downloadStage3Attendance = (districtId: string) => {
		setIsAttendanceDownloading(true);
		downloadReport(token, 'stage3-attendance', {
			district_id: districtId,
			attendance_rows_per_page: attendanceLayoutConfig.rowsPerPage,
			attendance_row_height: attendanceLayoutConfig.rowHeight,
			attendance_separator_row_height: attendanceLayoutConfig.separatorRowHeight,
		})
			.then((response) => {
				const disposition = response.headers?.['content-disposition'] as string | undefined;
				const match = disposition ? disposition.match(/filename="?([^"]+)"?/i) : null;
				const fileName = match?.[1] || 'Lista_obecnosci_Etap-III.xlsx';

				const url = window.URL.createObjectURL(response.data);
				const a = document.createElement('a');
				a.style.display = 'none';
				a.setAttribute('download', fileName);
				a.href = url;
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				window.URL.revokeObjectURL(url);
				setMsg({ msg: 'Pobrano listę obecności', severity: 'success' });
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response?.status === 401 || exception.response?.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					const msg = exception?.response?.data?.message
						? exception?.response?.data?.message
						: 'Błąd serwera. Spróbuj za chwilę';
					setMsg({ msg });
				}
			})
			.finally(() => {
				setIsAttendanceDownloading(false);
			});
	};

	const generateCodesOnly = () => {
		setIsCodesGenerating(true);
		generateStage3WorkCodes(token, false)
			.then((response) => {
				if (response.status === 200) {
					const created = Number(response?.data?.created || 0);
					const updated = Number(response?.data?.updated || 0);
					const qualified = Number(response?.data?.qualified_students || 0);
					setMsg({
						msg: `Kody gotowe. Zakwalifikowani: ${qualified}, nowe: ${created}, zaktualizowane: ${updated}`,
						severity: 'success',
					});
					refresh();
				} else {
					setMsg({ msg: response.data?.message || 'Błąd serwera. Spróbuj za chwilę' });
				}
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response?.status === 401 || exception.response?.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					const msg = exception?.response?.data?.message
						? exception?.response?.data?.message
						: 'Błąd serwera. Spróbuj za chwilę';
					setMsg({ msg });
				}
			})
			.finally(() => {
				setIsCodesGenerating(false);
			});
	};

	const handleCodesDistrictChange = (event: SelectChangeEvent<string>) => {
		setSelectedCodesDistrict(event.target.value);
	};

	const hydrateCodesPresetEditor = useCallback((slug: string, presets: Stage3CodePrintPreset[]) => {
		const selected = presets.find((preset) => preset.slug === slug) || presets[0];
		if (!selected) {
			setCodesPresetSlugInput(slug || 'default');
			setCodesPresetLabelInput('');
			setCodesPresetDraftConfig(normalizeCodesPresetDraftConfig(DEFAULT_CODES_PRESET_CONFIG));
			setCodesPresetSavedSnapshot(null);
			return;
		}
		setSelectedCodesPrintProfile(selected.slug);
		setCodesPresetSlugInput(selected.slug);
		const normalizedConfig = normalizeCodesPresetDraftConfig(selected.config);
		const normalizedLabel = selected.label || selected.slug;
		setCodesPresetLabelInput(normalizedLabel);
		setCodesPresetDraftConfig(normalizedConfig);
		setCodesPresetSavedSnapshot({
			slug: selected.slug,
			label: normalizedLabel,
			config: normalizedConfig,
		});
	}, []);

	const loadCodesPresets = useCallback(
		(preserveUnsavedDraft: boolean = false) => {
			setIsCodesPresetsLoading(true);
			getStage3CodePrintPresets(token)
				.then((response) => {
					if (response.status !== 200) {
						setMsg({ msg: response.data?.message || 'Nie udało się pobrać presetów wydruku.' });
						return;
					}
					const presets = Array.isArray(response.data?.presets) ? response.data.presets : [];
					const normalized: Stage3CodePrintPreset[] = presets.map((preset: any) => ({
						slug: String(preset?.slug || ''),
						label: String(preset?.label || preset?.slug || ''),
						config: (preset?.config || {}) as Record<string, any>,
					}));
					const defaultSlug = String(response.data?.default_preset_slug || 'default');
					setCodesPresets(normalized);
					setCodesDefaultPresetSlug(defaultSlug);
					if (preserveUnsavedDraft && hasCodesPresetUnsavedChanges) {
						return;
					}
					const fallback =
						normalized.find((preset) => preset.slug === defaultSlug)?.slug || normalized[0]?.slug || defaultSlug;
					hydrateCodesPresetEditor(fallback, normalized);
				})
				.catch((exception) => {
					console.error(exception);
					if (exception.response?.status === 401 || exception.response?.status === 403) {
						logout();
						navigate(loginPage);
						setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
					} else {
						setMsg({ msg: exception?.response?.data?.message || 'Błąd serwera. Spróbuj za chwilę' });
					}
				})
				.finally(() => {
					setIsCodesPresetsLoading(false);
				});
		},
		[hasCodesPresetUnsavedChanges, hydrateCodesPresetEditor, logout, navigate, setMsg, token],
	);

	const saveCodesPreset = () => {
		setIsCodesPresetSaving(true);
		saveStage3CodePrintPreset(token, {
			slug: codesPresetSlugInput,
			label: codesPresetLabelInput,
			config: buildCodesPresetPayloadConfig(),
			set_as_default: codesSetAsDefaultOnSave,
		})
			.then((response) => {
				if (response.status !== 200) {
					setMsg({ msg: response.data?.message || 'Nie udało się zapisać presetu.' });
					return;
				}
				const presets = Array.isArray(response.data?.presets) ? response.data.presets : [];
				const normalized: Stage3CodePrintPreset[] = presets.map((preset: any) => ({
					slug: String(preset?.slug || ''),
					label: String(preset?.label || preset?.slug || ''),
					config: (preset?.config || {}) as Record<string, any>,
				}));
				const defaultSlug = String(response.data?.default_preset_slug || codesDefaultPresetSlug);
				setCodesPresets(normalized);
				setCodesDefaultPresetSlug(defaultSlug);
				hydrateCodesPresetEditor(String(response.data?.saved_preset?.slug || codesPresetSlugInput), normalized);
				setCodesSetAsDefaultOnSave(false);
				setMsg({ msg: 'Preset wydruku został zapisany.', severity: 'success' });
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response?.status === 401 || exception.response?.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: exception?.response?.data?.message || 'Błąd serwera. Spróbuj za chwilę' });
				}
			})
			.finally(() => {
				setIsCodesPresetSaving(false);
			});
	};

	const setSelectedCodesPresetAsDefault = () => {
		if (!selectedCodesPrintProfile) {
			return;
		}
		setIsCodesPresetSaving(true);
		setStage3CodePrintDefaultPreset(token, selectedCodesPrintProfile)
			.then((response) => {
				if (response.status !== 200) {
					setMsg({ msg: response.data?.message || 'Nie udało się ustawić domyślnego presetu.' });
					return;
				}
				const defaultSlug = String(response.data?.default_preset_slug || selectedCodesPrintProfile);
				setCodesDefaultPresetSlug(defaultSlug);
				setMsg({ msg: 'Ustawiono domyślny preset wydruku.', severity: 'success' });
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response?.status === 401 || exception.response?.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: exception?.response?.data?.message || 'Błąd serwera. Spróbuj za chwilę' });
				}
			})
			.finally(() => {
				setIsCodesPresetSaving(false);
			});
	};

	useEffect(() => {
		if (!olympics?.secondBStageQualificationPublished) {
			setEligibleCodeDistricts([]);
			return;
		}

		getThirdAStageStudents(token)
			.then((response) => {
				if (response.status !== 200 || !Array.isArray(response.data)) {
					setEligibleCodeDistricts([]);
					return;
				}

				const byId = new Map<number, District>();
				response.data.forEach((row: any) => {
					const district = row?.district;
					if (!district?.id || !district?.name) {
						return;
					}
					if (!byId.has(Number(district.id))) {
						byId.set(Number(district.id), {
							id: Number(district.id),
							name: String(district.name),
						} as District);
					}
				});

				const filtered = districts.filter((d) => byId.has(Number(d.id))).sort((a, b) => a.name.localeCompare(b.name));

				setEligibleCodeDistricts(filtered);
			})
			.catch(() => {
				setEligibleCodeDistricts([]);
			});
	}, [olympics?.secondBStageQualificationPublished, token, districts]);

	const openCodesDialog = () => {
		setSelectedCodesDistrict('all');
		setCodesSetAsDefaultOnSave(false);
		setIsCodesDialogOpen(true);
		loadCodesPresets(true);
	};

	const confirmCodesDownload = () => {
		setIsCodesDialogOpen(false);
		downloadStage3Codes(selectedCodesDistrict, selectedCodesPrintProfile, buildCodesPresetPayloadConfig());
	};

	const handleAttendanceDistrictChange = (event: SelectChangeEvent<string>) => {
		setSelectedAttendanceDistrict(event.target.value);
	};

	const handleCodesPrintProfileChange = (event: SelectChangeEvent<string>) => {
		const slug = event.target.value;
		setSelectedCodesPrintProfile(slug);
		hydrateCodesPresetEditor(slug, codesPresets);
	};

	useEffect(() => {
		if (typeof window === 'undefined') {
			return;
		}
		window.sessionStorage.setItem('stage3_codes_grid_test_enabled', isCodesGridTestEnabled ? '1' : '0');
	}, [isCodesGridTestEnabled]);

	useEffect(() => {
		if (typeof window === 'undefined') {
			return;
		}
		window.localStorage.setItem('stage3_attendance_layout_config', JSON.stringify(attendanceLayoutConfig));
	}, [attendanceLayoutConfig]);

	const openAttendanceDialog = () => {
		setSelectedAttendanceDistrict('all');
		setIsAttendanceDialogOpen(true);
	};

	const confirmAttendanceDownload = () => {
		setIsAttendanceDialogOpen(false);
		downloadStage3Attendance(selectedAttendanceDistrict);
	};

	const openJuryPairsDialog = () => {
		setIsJuryPairsDialogOpen(true);
	};

	const openDocumentAccessDialog = () => {
		setIsDocumentAccessDialogOpen(true);
	};

	const renderSettings = () => {
		return (
			<Box
				sx={{
					maxHeight: 'calc(100vh - var(--panel-header-height, 0px) - 48px)',
					overflowY: 'auto',
					overflowX: 'hidden',
					pr: { xs: 0, sm: 1 },
				}}
			>
				<Grid
					item
					container
					flexWrap={{ xs: 'wrap', md: 'nowrap' }}
					sx={{
						maxWidth: '100%',
						width: '100%',
						boxSizing: 'border-box',
						columnGap: { xs: '0', md: '50px' },
						rowGap: { xs: '30px', md: '0' },
					}}
				>
					<Grid
						item
						container
						flexDirection={'column'}
						sx={{ minWidth: 0, flex: 1 }}
					>
						<Typography
							mt={2}
							variant={'h5'}
						>
							Aktywna edycja: {olympics?.edition || '—'}
						</Typography>
						<Typography
							variant={'body2'}
							sx={{ mb: 1, color: isHighContrast ? palette.primary.light : 'text.secondary' }}
						>
							Listy w panelu i ustawienia poniżej dotyczą tej edycji. Poprzednie sezony zostają w bazie (przełączanie
							edycji w rejestrze — w planie). Publikacje wyników po nowej edycji startują odznaczone — to normalne.
						</Typography>
						<Typography
							mt={5}
							variant={'h4'}
						>
							Ustawienia rejestracji
						</Typography>
						<Divider sx={{ mt: 2, mb: 2, borderColor: isHighContrast ? palette.primary.main : 'inherit' }} />
						<LocalizationProvider
							dateAdapter={AdapterDayjs}
							adapterLocale='pl'
						>
							<DateTimePicker
								label='Koniec rejestracji nauczycieli'
								value={olympics?.teachersRegistrationFinishDate}
								onChange={(newValue) => {
									if (olympics) {
										setOlympics({ ...olympics, teachersRegistrationFinishDate: newValue });
									}
								}}
								sx={{
									mt: 3,
									maxWidth: '50%',
									fontSize: '12px',
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& label': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
							<FormControlLabel
								control={
									<Checkbox
										checked={olympics?.isStudentRegisterEnabled}
										onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
											if (olympics) {
												setOlympics({ ...olympics, isStudentRegisterEnabled: event.target.checked });
											}
										}}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'&.Mui-checked': {
												color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											},
										}}
									/>
								}
								label='Włącz rejestrację uczniów'
							/>
							<DateTimePicker
								label='Koniec rejestracji uczniów'
								value={olympics?.studentsRegistrationFinishDate}
								onChange={(newValue) => {
									if (olympics) {
										setOlympics({ ...olympics, studentsRegistrationFinishDate: newValue });
									}
								}}
								sx={{
									mt: 3,
									maxWidth: '50%',
									fontSize: '12px',
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& label': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
							<DateTimePicker
								label='Koniec potwierdzania uczestników'
								value={olympics?.registrationConfirmationFinishDate}
								onChange={(newValue) => {
									if (olympics) {
										setOlympics({ ...olympics, registrationConfirmationFinishDate: newValue });
									}
								}}
								sx={{
									mt: 3,
									maxWidth: '50%',
									fontSize: '12px',
									color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
									'& *': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& label': {
										color: isHighContrast ? palette.primary.main : 'inherit',
									},
									'& .MuiOutlinedInput-root': {
										'&:hover fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& fieldset': {
											borderColor: isHighContrast ? palette.primary.main : 'inherit',
										},
									},
								}}
							/>
						</LocalizationProvider>
						<Typography
							mt={5}
							variant={'h4'}
						>
							Ustawienia etapów
						</Typography>
						<Divider sx={{ mt: 2, borderColor: isHighContrast ? palette.primary.main : 'inherit' }} />
						<Typography mt={5}>Etap I</Typography>
						<Typography
							mt={2}
							variant='body2'
							sx={{ fontStyle: 'italic', fontWeight: 600 }}
						>
							Ręczna kwalifikacja uczniów
						</Typography>
						<SettingsRow
							firstChild={
								<TextField
									type={'number'}
									label={'Maksymalna liczba punktów'}
									fullWidth
									value={olympics?.firstStageMaxPoints}
									onChange={(event) => handlePointsChange(event, 'firstStageMaxPoints')}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
						/>
						<Typography mt={5}>Etap IIA</Typography>
						<Typography
							mt={2}
							variant='body2'
							sx={{ fontStyle: 'italic', fontWeight: 600 }}
						>
							Kwalifikacja na podstawie limitu punktów
						</Typography>
						<Typography
							mt={2}
							variant='body2'
							sx={{ fontStyle: 'italic' }}
						>
							Wariant 1
						</Typography>
						<SettingsRow
							firstChild={
								<TextField
									type={'number'}
									label={'Część pisemna nr 1'}
									fullWidth
									value={olympics?.secondStagePoints.partA.v1.part1}
									onChange={(event) => handlePointsChange(event, 'secondStagePoints.partA.v1.part1')}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
							secondChild={
								<TextField
									type={'number'}
									label={'Część pisemna nr 2'}
									fullWidth
									value={olympics?.secondStagePoints.partA.v1.part2}
									onChange={(event) => handlePointsChange(event, 'secondStagePoints.partA.v1.part2')}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
						/>
						<SettingsRow
							firstChild={
								<TextField
									type={'number'}
									label={'Maks. część pisemna nr 1'}
									fullWidth
									value={olympics?.secondStagePoints.partA.max.v1.part1}
									onChange={(event) => handlePointsChange(event, 'secondStagePoints.partA.max.v1.part1')}
									error={isMaxInvalid(
										olympics?.secondStagePoints.partA.max.v1.part1,
										olympics?.secondStagePoints.partA.v1.part1,
									)}
									helperText={maxHelperText(
										olympics?.secondStagePoints.partA.max.v1.part1,
										olympics?.secondStagePoints.partA.v1.part1,
									)}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
							secondChild={
								<TextField
									type={'number'}
									label={'Maks. część pisemna nr 2'}
									fullWidth
									value={olympics?.secondStagePoints.partA.max.v1.part2}
									onChange={(event) => handlePointsChange(event, 'secondStagePoints.partA.max.v1.part2')}
									error={isMaxInvalid(
										olympics?.secondStagePoints.partA.max.v1.part2,
										olympics?.secondStagePoints.partA.v1.part2,
									)}
									helperText={maxHelperText(
										olympics?.secondStagePoints.partA.max.v1.part2,
										olympics?.secondStagePoints.partA.v1.part2,
									)}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
						/>
						<Typography
							mt={2}
							variant='body2'
							sx={{ fontStyle: 'italic' }}
						>
							Wariant 2
						</Typography>
						<SettingsRow
							firstChild={
								<TextField
									type={'number'}
									label={'Część pisemna nr 1'}
									fullWidth
									value={olympics?.secondStagePoints.partA.v2.part1}
									onChange={(event) => handlePointsChange(event, 'secondStagePoints.partA.v2.part1')}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
							secondChild={
								<TextField
									type={'number'}
									label={'Część pisemna nr 2'}
									fullWidth
									value={olympics?.secondStagePoints.partA.v2.part2}
									onChange={(event) => handlePointsChange(event, 'secondStagePoints.partA.v2.part2')}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
						/>
						<SettingsRow
							firstChild={
								<TextField
									type={'number'}
									label={'Maks. część pisemna nr 1'}
									fullWidth
									value={olympics?.secondStagePoints.partA.max.v2.part1}
									onChange={(event) => handlePointsChange(event, 'secondStagePoints.partA.max.v2.part1')}
									error={isMaxInvalid(
										olympics?.secondStagePoints.partA.max.v2.part1,
										olympics?.secondStagePoints.partA.v2.part1,
									)}
									helperText={maxHelperText(
										olympics?.secondStagePoints.partA.max.v2.part1,
										olympics?.secondStagePoints.partA.v2.part1,
									)}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
							secondChild={
								<TextField
									type={'number'}
									label={'Maks. część pisemna nr 2'}
									fullWidth
									value={olympics?.secondStagePoints.partA.max.v2.part2}
									onChange={(event) => handlePointsChange(event, 'secondStagePoints.partA.max.v2.part2')}
									error={isMaxInvalid(
										olympics?.secondStagePoints.partA.max.v2.part2,
										olympics?.secondStagePoints.partA.v2.part2,
									)}
									helperText={maxHelperText(
										olympics?.secondStagePoints.partA.max.v2.part2,
										olympics?.secondStagePoints.partA.v2.part2,
									)}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
						/>
						<Typography
							mt={2}
							variant='body2'
							sx={{ fontStyle: 'italic' }}
						>
							Łączny limit z części pisemnej
						</Typography>
						<SettingsRow
							firstChild={
								<TextField
									type={'number'}
									label={'Łączny limit z części 1 i 2'}
									fullWidth
									value={olympics?.secondStagePoints.partA.total}
									onChange={(event) => handlePointsChange(event, 'secondStagePoints.partA.total')}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
						/>
						<Typography mt={5}>Etap IIB</Typography>
						<Typography
							mt={2}
							variant='body2'
							sx={{ fontStyle: 'italic', fontWeight: 600 }}
						>
							Kwalifikacja na podstawie decyzji administratora
						</Typography>
						<SettingsRow
							firstChild={
								!isSmallOlympic ? (
									<TextField
										type={'number'}
										label={'Część ustna nr 1'}
										fullWidth
										value={olympics?.secondStagePoints.partB.part1}
										onChange={(event) => handlePointsChange(event, 'secondStagePoints.partB.part1')}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'& *': {
												color: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& .MuiOutlinedInput-root': {
												'&:hover fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
												'& fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
											},
										}}
									/>
								) : (
									<TextField
										type={'number'}
										label={'Punkty IIB'}
										fullWidth
										value={olympics?.secondStagePoints.partB.part3}
										onChange={(event) => handlePointsChange(event, 'secondStagePoints.partB.part3')}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'& *': {
												color: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& .MuiOutlinedInput-root': {
												'&:hover fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
												'& fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
											},
										}}
									/>
								)
							}
							secondChild={
								!isSmallOlympic ? (
									<TextField
										type={'number'}
										label={'Część ustna nr 2'}
										fullWidth
										value={olympics?.secondStagePoints.partB.part2}
										onChange={(event) => handlePointsChange(event, 'secondStagePoints.partB.part2')}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'& *': {
												color: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& .MuiOutlinedInput-root': {
												'&:hover fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
												'& fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
											},
										}}
									/>
								) : undefined
							}
							thirdChild={
								!isSmallOlympic ? (
									<TextField
										label={'Część ustna nr 3'}
										fullWidth
										value={olympics?.secondStagePoints.partB.part3}
										onChange={(event) => handlePointsChange(event, 'secondStagePoints.partB.part3')}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'& *': {
												color: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& .MuiOutlinedInput-root': {
												'&:hover fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
												'& fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
											},
										}}
									/>
								) : undefined
							}
						/>
						<SettingsRow
							firstChild={
								!isSmallOlympic ? (
									<TextField
										type={'number'}
										label={'Maks. część ustna nr 1'}
										fullWidth
										value={olympics?.secondStagePoints.partB.max.part1}
										onChange={(event) => handlePointsChange(event, 'secondStagePoints.partB.max.part1')}
										error={isMaxInvalid(
											olympics?.secondStagePoints.partB.max.part1,
											olympics?.secondStagePoints.partB.part1,
										)}
										helperText={maxHelperText(
											olympics?.secondStagePoints.partB.max.part1,
											olympics?.secondStagePoints.partB.part1,
										)}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'& *': {
												color: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& .MuiOutlinedInput-root': {
												'&:hover fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
												'& fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
											},
										}}
									/>
								) : (
									<TextField
										type={'number'}
										label={'Maks. punkty IIB'}
										fullWidth
										value={olympics?.secondStagePoints.partB.max.part3}
										onChange={(event) => handlePointsChange(event, 'secondStagePoints.partB.max.part3')}
										error={isMaxInvalid(
											olympics?.secondStagePoints.partB.max.part3,
											olympics?.secondStagePoints.partB.part3,
										)}
										helperText={maxHelperText(
											olympics?.secondStagePoints.partB.max.part3,
											olympics?.secondStagePoints.partB.part3,
										)}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'& *': {
												color: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& .MuiOutlinedInput-root': {
												'&:hover fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
												'& fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
											},
										}}
									/>
								)
							}
							secondChild={
								!isSmallOlympic ? (
									<TextField
										type={'number'}
										label={'Maks. część ustna nr 2'}
										fullWidth
										value={olympics?.secondStagePoints.partB.max.part2}
										onChange={(event) => handlePointsChange(event, 'secondStagePoints.partB.max.part2')}
										error={isMaxInvalid(
											olympics?.secondStagePoints.partB.max.part2,
											olympics?.secondStagePoints.partB.part2,
										)}
										helperText={maxHelperText(
											olympics?.secondStagePoints.partB.max.part2,
											olympics?.secondStagePoints.partB.part2,
										)}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'& *': {
												color: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& .MuiOutlinedInput-root': {
												'&:hover fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
												'& fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
											},
										}}
									/>
								) : undefined
							}
							thirdChild={
								!isSmallOlympic ? (
									<TextField
										label={'Maks. część ustna nr 3'}
										fullWidth
										value={olympics?.secondStagePoints.partB.max.part3}
										onChange={(event) => handlePointsChange(event, 'secondStagePoints.partB.max.part3')}
										error={isMaxInvalid(
											olympics?.secondStagePoints.partB.max.part3,
											olympics?.secondStagePoints.partB.part3,
										)}
										helperText={maxHelperText(
											olympics?.secondStagePoints.partB.max.part3,
											olympics?.secondStagePoints.partB.part3,
										)}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'& *': {
												color: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& .MuiOutlinedInput-root': {
												'&:hover fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
												'& fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
											},
										}}
									/>
								) : undefined
							}
						/>
						<Typography
							mt={2}
							variant='body2'
							sx={{ fontStyle: 'italic' }}
						>
							Łączny limit punktów z części pisemnej i ustnej
						</Typography>
						<SettingsRow
							firstChild={
								<TextField
									type={'number'}
									label={'Łączny limit z części IIA i IIB'}
									fullWidth
									value={olympics?.secondStagePoints.partB.total}
									onChange={(event) => handlePointsChange(event, 'secondStagePoints.partB.total')}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
						/>
						<Typography
							mt={2}
							variant='body2'
							sx={{ fontStyle: 'italic' }}
						>
							Limit liczby uczniów
						</Typography>
						<SettingsRow
							firstChild={
								<TextField
									type={'number'}
									label={'Limit liczby uczniów'}
									fullWidth
									value={olympics?.secondStagePoints.partB.totalStudentsLimit}
									onChange={(event) => handlePointsChange(event, 'secondStagePoints.partB.totalStudentsLimit')}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
						/>
						<SettingsRow
							firstChild={
								<LocalizationProvider
									dateAdapter={AdapterDayjs}
									adapterLocale='pl'
								>
									<DateTimePicker
										label='Koniec wprowadzania wyników przez sekretarzy'
										value={olympics?.secondStagePoints?.partB?.stageClosedDate}
										onChange={(newValue) => {
											if (olympics) {
												handleValueChange('secondStagePoints.partB.stageClosedDate', newValue);
											}
										}}
										sx={{
											mt: 3,
											width: '100%',
											fontSize: '12px',
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'& *': {
												color: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& .MuiOutlinedInput-root': {
												'&:hover fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
												'& fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
											},
										}}
									/>
								</LocalizationProvider>
							}
						/>
						<Typography mt={5}>Etap IIIA</Typography>
						<SettingsRow
							firstChild={
								<TextField
									type={'number'}
									label={'Część pisemna'}
									fullWidth
									value={olympics?.thirdStagePoints.partA.points}
									onChange={(event) => handlePointsChange(event, 'thirdStagePoints.partA.points')}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
						/>
						<SettingsRow
							firstChild={
								<TextField
									type={'number'}
									label={'Maks. część pisemna'}
									fullWidth
									value={olympics?.thirdStagePoints.partA.maxPoints}
									onChange={(event) => handlePointsChange(event, 'thirdStagePoints.partA.maxPoints')}
									error={isMaxInvalid(
										olympics?.thirdStagePoints.partA.maxPoints,
										olympics?.thirdStagePoints.partA.points,
									)}
									helperText={maxHelperText(
										olympics?.thirdStagePoints.partA.maxPoints,
										olympics?.thirdStagePoints.partA.points,
									)}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
						/>
						<Typography
							mt={2}
							variant='body2'
							sx={{ fontStyle: 'italic', fontWeight: 600 }}
						>
							Finalista
						</Typography>
						<SettingsRow
							firstChild={
								<TextField
									type={'number'}
									label={'Limit punktów dla Finalisty'}
									fullWidth
									value={olympics?.thirdStagePoints.partA.finalistPoints}
									onChange={(event) => handlePointsChange(event, 'thirdStagePoints.partA.finalistPoints')}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
						/>
						<Typography
							mt={3}
							variant='body2'
							sx={{ fontStyle: 'italic', fontWeight: 600 }}
						>
							Okręg Zagranica — Finalista (część ustna IIIB)
						</Typography>
						<Typography
							variant='caption'
							display='block'
							sx={{ mb: 1, maxWidth: 720 }}
						>
							Włączone: tytuł Finalisty dla Zagranicy wymaga minimalnej sumy punktów z obu części ustnych IIIB.
							Wyłączone: jak pozostali uczniowie — limit z części pisemnej IIIA (powyżej).
						</Typography>
						<SettingsRow
							firstChild={
								<FormControlLabel
									control={
										<Checkbox
											checked={!!olympics?.foreignFinalistRequiresIIIb}
											onChange={(e) => handleValueChange('foreignFinalistRequiresIIIb', e.target.checked)}
										/>
									}
									label='Zagranica: Finalista wymaga sumy ustnych IIIB (I + II)'
								/>
							}
						/>
						<SettingsRow
							firstChild={
								<TextField
									type={'number'}
									label={'Minimalna suma punktów IIIB (Zagranica)'}
									fullWidth
									disabled={!olympics?.foreignFinalistRequiresIIIb}
									value={olympics?.foreignFinalistIIIbMinPoints ?? 20}
									onChange={(event) => handleValueChange('foreignFinalistIIIbMinPoints', Number(event.target.value))}
									inputProps={{ min: 0, max: 200 }}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
						/>
						<Typography mt={5}>Etap IIIB</Typography>
						<Typography
							mt={2}
							variant='body2'
							sx={{ fontStyle: 'italic', fontWeight: 600 }}
						>
							Laureaci
						</Typography>
						<Typography
							variant='caption'
							display='block'
							sx={{ mb: 1 }}
						>
							Progi Laureata I–III stopnia: suma punktów całego etapu III (IIIA + obie części ustne IIIB).
						</Typography>
						<SettingsRow
							firstChild={
								<TextField
									type={'number'}
									label={'Laureat III stopnia'}
									fullWidth
									value={olympics?.thirdStagePoints.partB.laureate3}
									onChange={(event) => handlePointsChange(event, 'thirdStagePoints.partB.laureate3')}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
							secondChild={
								<TextField
									type={'number'}
									label={'Laureat II stopnia'}
									fullWidth
									value={olympics?.thirdStagePoints.partB.laureate2}
									onChange={(event) => handlePointsChange(event, 'thirdStagePoints.partB.laureate2')}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
							thirdChild={
								<TextField
									type={'number'}
									label={'Laureat I stopnia'}
									fullWidth
									value={olympics?.thirdStagePoints.partB.laureate1}
									onChange={(event) => handlePointsChange(event, 'thirdStagePoints.partB.laureate1')}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
						/>
						<SettingsRow
							firstChild={
								<TextField
									type={'number'}
									label={'Maks. punkty I'}
									fullWidth
									value={olympics?.thirdStagePoints.partB.max.part1}
									onChange={(event) => handlePointsChange(event, 'thirdStagePoints.partB.max.part1')}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
							secondChild={
								<TextField
									type={'number'}
									label={'Maks. punkty II'}
									fullWidth
									value={olympics?.thirdStagePoints.partB.max.part2}
									onChange={(event) => handlePointsChange(event, 'thirdStagePoints.partB.max.part2')}
									sx={{
										color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
										'& *': {
											color: isHighContrast ? palette.primary.main : 'inherit',
										},
										'& .MuiOutlinedInput-root': {
											'&:hover fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& fieldset': {
												borderColor: isHighContrast ? palette.primary.main : 'inherit',
											},
										},
									}}
								/>
							}
						/>
					</Grid>
					<Grid
						item
						container
						flexDirection={'column'}
						sx={{ minWidth: 0, flex: 1 }}
					>
						<Typography
							mt={5}
							variant={'h4'}
						>
							Opcje
						</Typography>
						<Divider sx={{ mt: 2, mb: 2, borderColor: isHighContrast ? palette.primary.main : 'inherit' }} />
						<Button
							startIcon={<SaveIcon />}
							variant={'contained'}
							onClick={() => {
								setDialogContent('Czy napewno chcesz zmienić ustawienia olimpiady?');
								setOkFunction(() => updateOlympics);
							}}
						>
							Zapisz zmiany
						</Button>
						<Typography
							mt={5}
							variant={'h4'}
						>
							Publikacja wyników
						</Typography>
						<Divider sx={{ mt: 2, mb: 2, borderColor: isHighContrast ? palette.primary.main : 'inherit' }} />
						<Tooltip title={publishTooltip('Udostępnia uczestnikom wyniki etapu I')}>
							<FormControlLabel
								control={
									<Checkbox
										checked={Boolean(olympics?.firstStagePublished)}
										disabled={!publishEditEnabled}
										onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
											handleValueChange('firstStagePublished', event.target.checked);
										}}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'&.Mui-checked': {
												color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											},
										}}
									/>
								}
								label='Opublikowany etap I'
							/>
						</Tooltip>
						<Tooltip title={publishTooltip('Udostępnia uczestnikom wyniki etapu IIA')}>
							<FormControlLabel
								control={
									<Checkbox
										checked={Boolean(olympics?.secondAStagePublished)}
										disabled={!publishEditEnabled}
										onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
											handleValueChange('secondAStagePublished', event.target.checked);
										}}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'&.Mui-checked': {
												color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											},
										}}
									/>
								}
								label='Opublikowany etap IIA'
							/>
						</Tooltip>
						<Tooltip title={publishTooltip('Udostępnia uczestnikom wyniki etapu IIB')}>
							<FormControlLabel
								control={
									<Checkbox
										checked={Boolean(olympics?.secondBStagePublished)}
										disabled={!publishEditEnabled}
										onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
											handleValueChange('secondBStagePublished', event.target.checked);
										}}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'&.Mui-checked': {
												color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											},
										}}
									/>
								}
								label='Opublikowany etap IIB'
							/>
						</Tooltip>
						<Tooltip title={publishTooltip('Udostępnia uczestnikom charakterystykę z etapu IIB')}>
							<FormControlLabel
								control={
									<Checkbox
										checked={Boolean(olympics?.secondBOralDescriptionPublished)}
										disabled={!publishEditEnabled}
										onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
											handleValueChange('secondBOralDescriptionPublished', event.target.checked);
										}}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'&.Mui-checked': {
												color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											},
										}}
									/>
								}
								label='Opublikowana charakterystyka etapu IIB'
							/>
						</Tooltip>
						<Tooltip title={publishTooltip('Udostępnia informację o kwalifikacji do etapu III')}>
							<FormControlLabel
								control={
									<Checkbox
										checked={Boolean(olympics?.secondBStageQualificationPublished)}
										disabled={!publishEditEnabled}
										onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
											handleValueChange('secondBStageQualificationPublished', event.target.checked);
										}}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'&.Mui-checked': {
												color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											},
										}}
									/>
								}
								label='Opublikowana kwalifikacja do etapu III'
							/>
						</Tooltip>
						<Tooltip title={publishTooltip('Udostępnia uczestnikom wyniki etapu IIIA')}>
							<FormControlLabel
								control={
									<Checkbox
										checked={Boolean(olympics?.thirdAStagePublished)}
										disabled={!publishEditEnabled}
										onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
											handleValueChange('thirdAStagePublished', event.target.checked);
										}}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'&.Mui-checked': {
												color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											},
										}}
									/>
								}
								label='Opublikowany etap IIIA'
							/>
						</Tooltip>
						<Tooltip title={publishTooltip('Udostępnia uczestnikom wyniki etapu IIIB')}>
							<FormControlLabel
								control={
									<Checkbox
										checked={Boolean(olympics?.thirdBStagePublished)}
										disabled={!publishEditEnabled}
										onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
											handleValueChange('thirdBStagePublished', event.target.checked);
										}}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'&.Mui-checked': {
												color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											},
										}}
									/>
								}
								label='Opublikowany etap IIIB'
							/>
						</Tooltip>
						<Button
							variant={'outlined'}
							onClick={() => setPublishEditEnabled((prev) => !prev)}
							sx={{ mt: 1, whiteSpace: 'nowrap' }}
						>
							{publishEditEnabled ? 'Zablokuj edycję publikacji' : 'Edytuj publikacje'}
						</Button>
						{!olympics?.secondBStageQualificationPublished && (
							<Tooltip
								title={
									isRecalculateDisabled
										? 'Dostępne po publikacji etapu IIA i przed opublikowaniem kwalifikacji'
										: 'Uruchamia przeliczenie kwalifikacji dla wszystkich uczniów'
								}
							>
								<span>
									<Button
										startIcon={<ReplayIcon />}
										variant={'outlined'}
										disabled={isRecalculateDisabled}
										onClick={() => {
											setDialogContent('Czy napewno chcesz przeliczyć kwalifikację do etapu III?');
											setOkFunction(() => recalculateQualification);
										}}
										sx={{ mt: 2 }}
									>
										Przelicz kwalifikację do etapu III
									</Button>
								</span>
							</Tooltip>
						)}
						{olympics?.secondBStageQualificationPublished && (
							<Stage3CodeActionsBar
								isCodesDownloading={isCodesDownloading}
								isAttendanceDownloading={isAttendanceDownloading}
								isCodesGenerating={isCodesGenerating}
								isJuryPairsLoading={false}
								onOpenCodesDialog={openCodesDialog}
								onGenerateCodesOnly={generateCodesOnly}
								onOpenAttendanceDialog={openAttendanceDialog}
								onOpenJuryPairsDialog={openJuryPairsDialog}
								onOpenDocumentAccessDialog={openDocumentAccessDialog}
							/>
						)}
						<Button
							startIcon={<ReplayIcon />}
							variant={'contained'}
							color={'warning'}
							onClick={() => setOpenNewOlympicsDialog(true)}
							sx={{
								mt: '100px',
								mb: 2,
								backgroundColor: isHighContrast ? palette.primary.main + '!important' : '',
								color: isHighContrast ? palette.primary.light + '!important' : '',
							}}
						>
							Nowa olimpiada
						</Button>
						<Typography>- tworzy nową edycję olimpiady (bez kasowania poprzedniej)</Typography>
						<Typography>- poprzedni sezon zostaje w bazie; listy panelu pokazują aktywną edycję</Typography>
						<Typography>
							- przed utworzeniem warto pobrać Excel z danymi bieżącej edycji (potem też można zmienić edycję i pobrać
							dane historyczne)
						</Typography>
						<Button
							variant='contained'
							sx={{ mt: 2, mb: 1, mr: 2 }}
							onClick={() => setOpenRspoRefreshDialog(true)}
						>
							Przeglądaj i zatwierdź różnice RSPO
						</Button>
						<Button
							variant='outlined'
							sx={{ mt: 2, mb: 1 }}
							onClick={async () => {
								try {
									const base = process.env.REACT_APP_API_URL || '';
									const res = await fetch(`${base}/olympics/schools/rspo-diff/csv`, {
										headers: { Authorization: `Bearer ${token}` },
									});
									if (!res.ok) throw new Error('HTTP ' + res.status);
									const blob = await res.blob();
									const url = URL.createObjectURL(blob);
									const a = document.createElement('a');
									a.href = url;
									a.download = 'schools-rspo-diff.csv';
									a.click();
									URL.revokeObjectURL(url);
									setMsg({ msg: 'Pobrano raport różnic szkół (RSPO).', severity: 'success' });
								} catch (e) {
									setMsg({ msg: 'Nie udało się pobrać raportu RSPO.', severity: 'error' });
								}
							}}
						>
							Pobierz raport różnic szkół (RSPO → CSV)
						</Button>
						<Button
							variant='outlined'
							color='warning'
							sx={{ mt: 2, mb: 1, ml: 2 }}
							onClick={async () => {
								const ok = window.confirm(
									'Przywrócić okręgi szkół z poprzedniej edycji (ten sam numer RSPO)? Mazowsze/Warszawa zostaną rozdzielone na I/II (lub Warszawa–Mazowsze) według kodu pocztowego. Nie używa mapowania województwa z samego ZIP.',
								);
								if (!ok) return;
								try {
									const result = await restoreSchoolDistrictsFromPreviousEdition(token);
									setMsg({
										msg: result.message || `Zaktualizowano ${result.updated} szkół.`,
										severity: 'success',
									});
									refresh();
									refreshTables();
								} catch {
									setMsg({ msg: 'Nie udało się przywrócić okręgów z poprzedniej edycji.', severity: 'error' });
								}
							}}
						>
							Przywróć okręgi z poprzedniej edycji (RSPO)
						</Button>
						<Typography
							variant='body2'
							sx={{ mb: 2 }}
						>
							Ręcznie poprawione szkoły nie są nadpisywane automatycznie — raport służy do decyzji przed startem
							rejestracji. Przywracanie okręgów kopiuje przypisanie z poprzedniej edycji po numerze RSPO (na test-mala
							po błędnej naprawie po ZIP); Mazowsze mapuje na I/II według kodu pocztowego.
						</Typography>
						<Typography
							variant='subtitle2'
							sx={{ mt: 2 }}
						>
							Skany opinii poradni (Google Drive)
						</Typography>
						<Typography
							variant='body2'
							sx={{ mb: 1 }}
						>
							Folder docelowy na skany wgrywane przez uczniów/rodziców na stronie Konta (po zaznaczeniu
							orzeczenia/opinii o dysgrafii przy rejestracji lub w panelu). OAuth Google Drive — Etap III → pary
							jurorskie → zakładka Google Drive setup (ten sam token co etap III). Bez folderu pliki idą lokalnie na
							serwer. W formularzu rejestracji nie ma uploadu — tylko checkbox.
						</Typography>
						<TextField
							fullWidth
							size='small'
							label='URL folderu Google Drive'
							value={opinionDriveFolder}
							onChange={(e) => setOpinionDriveFolder(e.target.value)}
							disabled={opinionDriveEnvLocked}
							helperText={
								opinionDriveEnvLocked
									? 'Wartość ustawiona w env.php (OLIJP_STUDENT_OPINION_DRIVE_FOLDER_URL).'
									: opinionDriveTokenOk
										? 'Token Drive skonfigurowany.'
										: 'Brak tokenu Drive — skonfiguruj OAuth w panelu Etap III.'
							}
							sx={{ mb: 1 }}
						/>
						<Button
							variant='outlined'
							disabled={opinionDriveEnvLocked || opinionDriveSaving}
							onClick={async () => {
								if (!token) return;
								setOpinionDriveSaving(true);
								try {
									await saveStudentOpinionDriveSettings(token, opinionDriveFolder.trim());
									setMsg({ msg: 'Zapisano folder skanów opinii.', severity: 'success' });
								} catch {
									setMsg({ msg: 'Nie udało się zapisać folderu Drive.', severity: 'error' });
								} finally {
									setOpinionDriveSaving(false);
								}
							}}
						>
							Zapisz folder opinii
						</Button>
						<Typography
							variant='subtitle2'
							sx={{ mt: 3 }}
						>
							Pomoc w panelu
						</Typography>
						<Typography
							variant='body2'
							sx={{ mb: 1 }}
						>
							Tekst wprowadzenia i linki widoczne na stronie Pomoc (nauczyciel, sekretarz, administrator).
						</Typography>
						<TextField
							fullWidth
							size='small'
							multiline
							minRows={2}
							label='Tekst wprowadzenia'
							value={helpIntro}
							onChange={(e) => setHelpIntro(e.target.value)}
							sx={{ mb: 2 }}
						/>
						{helpLinks.map((link, index) => (
							<Box
								key={`help-link-${index}`}
								sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}
							>
								<TextField
									size='small'
									label='Etykieta linku'
									value={link.label}
									onChange={(e) =>
										setHelpLinks((prev) =>
											prev.map((row, i) => (i === index ? { ...row, label: e.target.value } : row)),
										)
									}
								/>
								<TextField
									size='small'
									label='URL'
									value={link.url}
									onChange={(e) =>
										setHelpLinks((prev) => prev.map((row, i) => (i === index ? { ...row, url: e.target.value } : row)))
									}
								/>
								<TextField
									size='small'
									label='Role (np. teacher, secretary, administrator)'
									value={(link.roles || []).join(', ')}
									onChange={(e) =>
										setHelpLinks((prev) =>
											prev.map((row, i) =>
												i === index
													? {
															...row,
															roles: e.target.value
																.split(',')
																.map((r) => r.trim())
																.filter(Boolean),
														}
													: row,
											),
										)
									}
								/>
								<Button
									size='small'
									color='warning'
									onClick={() => setHelpLinks((prev) => prev.filter((_, i) => i !== index))}
								>
									Usuń link
								</Button>
							</Box>
						))}
						<Button
							variant='outlined'
							size='small'
							sx={{ mb: 1, mr: 1 }}
							onClick={() =>
								setHelpLinks((prev) => [
									...prev,
									{ label: '', url: '', roles: ['teacher', 'secretary', 'administrator'] },
								])
							}
						>
							Dodaj link
						</Button>
						<Button
							variant='contained'
							disabled={helpSaving}
							onClick={async () => {
								if (!token) return;
								setHelpSaving(true);
								try {
									await savePanelHelpSettings(token, { helpIntro: helpIntro.trim(), helpLinks });
									setMsg({ msg: 'Zapisano ustawienia pomocy.', severity: 'success' });
									refresh();
								} catch {
									setMsg({ msg: 'Nie udało się zapisać ustawień pomocy.', severity: 'error' });
								} finally {
									setHelpSaving(false);
								}
							}}
						>
							Zapisz pomoc
						</Button>
						<Grid
							item
							container
							flexDirection={'column'}
						>
							<Typography
								mt={5}
								variant={'h4'}
							>
								Dyplomy z etapu II
							</Typography>
							<Divider sx={{ mt: 1, mb: 2, borderColor: isHighContrast ? palette.primary.main : 'inherit' }} />
							<Button
								sx={{ mb: 2 }}
								startIcon={<AssignmentIcon />}
								variant={'contained'}
								onClick={() => {
									setDialogContent(
										'Wysłać dyplomy z części pisemnej etapu II (2A)? Dla uczniów z punktami zostaną wygenerowane pliki PDF i wysłane e-mailem na adresy przypisane do uczniów.',
									);
									setOkFunction(() => () => generateCertificates('written'));
								}}
							>
								Wyślij dyplomy — część pisemna (2A)
							</Button>
							<Button
								startIcon={<AssignmentIcon />}
								variant={'contained'}
								onClick={() => {
									setDialogContent(
										'Wysłać dyplomy z części ustnej etapu II (2B)? Dla uczniów z punktami zostaną wygenerowane pliki PDF i wysłane e-mailem na adresy przypisane do uczniów.',
									);
									setOkFunction(() => () => generateCertificates('oral'));
								}}
							>
								Wyślij dyplomy — część ustna (2B)
							</Button>
							<Alert
								severity='warning'
								sx={{ mt: 2, mb: 2, maxWidth: 720 }}
							>
								<Typography
									variant='body2'
									component='div'
								>
									<strong>Uwaga:</strong> po potwierdzeniu system dla uczniów z wprowadzonymi punktami w danej części{' '}
									<strong>tworzy pliki PDF</strong> i od razu <strong>wysyła je na adresy e-mail</strong> zapisane przy
									uczniu (poczta WordPress). Nie ma w tym widoku podglądu ani pobrania paczki — tylko masowa wysyłka.
									Przed uruchomieniem sprawdź poprawność danych i szablonów.
								</Typography>
							</Alert>
						</Grid>
						<Grid
							item
							container
							flexDirection={'column'}
						>
							<Typography
								mt={5}
								variant={'h4'}
							>
								Ustawienia generalne
							</Typography>
							<Divider sx={{ mt: 2, mb: 2, borderColor: isHighContrast ? palette.primary.main : 'inherit' }} />
							<SettingsRow
								firstChild={
									<TextField
										type={'text'}
										label={'Edycja Olimpiady'}
										fullWidth
										value={olympics?.edition}
										onChange={(newValue) => {
											if (olympics) {
												handleSettings('edition', newValue);
											}
										}}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'& *': {
												color: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& .MuiOutlinedInput-root': {
												'&:hover fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
												'& fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
											},
										}}
									/>
								}
							/>
							<SettingsRow
								firstChild={
									<TextField
										type={'text'}
										label={'Rok szkolny'}
										fullWidth
										value={olympics?.schoolYear}
										onChange={(newValue) => {
											if (olympics) {
												handleSettings('schoolYear', newValue);
											}
										}}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'& *': {
												color: isHighContrast ? palette.primary.main : 'inherit',
											},
											'& .MuiOutlinedInput-root': {
												'&:hover fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
												'& fieldset': {
													borderColor: isHighContrast ? palette.primary.main : 'inherit',
												},
											},
										}}
									/>
								}
							/>
						</Grid>
						<Grid
							item
							container
							flexDirection={'column'}
						>
							<Typography
								mt={5}
								variant={'h4'}
							>
								Ankieta etapu III
							</Typography>
							<Divider sx={{ mt: 2, mb: 2, borderColor: isHighContrast ? palette.primary.main : 'inherit' }} />
							<FormControlLabel
								control={
									<Checkbox
										checked={Boolean(olympics?.thirdStageQuestionnaireEnabled)}
										onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
											handleValueChange('thirdStageQuestionnaireEnabled', event.target.checked);
										}}
										sx={{
											color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											'&.Mui-checked': {
												color: isHighContrast ? palette.primary.main + '!important' : 'inherit',
											},
										}}
									/>
								}
								label='Dostęp do wypełniania ankiety przez uczniów'
								sx={{ mb: 1 }}
							/>
							<Typography
								variant='caption'
								sx={{
									color: olympics?.secondBStageQualificationPublished ? 'text.secondary' : 'error.main',
									display: 'block',
									mb: 1,
								}}
							>
								Wymaga opublikowanej kwalifikacji do etapu III.
							</Typography>
							<Accordion
								expanded={isSurveyPreviewOpen}
								onChange={handleSurveyPreviewChange}
								sx={{
									backgroundColor: 'transparent',
									boxShadow: 'none',
									border: '1px solid',
									borderColor: isHighContrast ? palette.primary.main : 'divider',
								}}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									sx={{ px: 2 }}
								>
									<Typography
										variant='subtitle1'
										color={palette.primary.main}
									>
										Podgląd pytań i tematów
									</Typography>
								</AccordionSummary>
								<AccordionDetails sx={{ px: 2, pb: 2 }}>
									{isSurveyPreviewLoading && <CircularProgress size={24} />}
									{!isSurveyPreviewLoading && !surveyPreviewLoaded && (
										<Typography variant='body2'>
											Nie udało się wczytać danych. Zwiń i rozwiń, aby spróbować ponownie.
										</Typography>
									)}
									{!isSurveyPreviewLoading && surveyPreviewLoaded && (
										<Grid
											item
											container
											flexDirection={'column'}
											gap={2}
										>
											<Divider sx={{ mt: 1 }} />
											<Grid
												item
												container
												flexDirection={'column'}
											>
												<Typography
													variant='subtitle1'
													sx={{ fontWeight: 600 }}
												>
													Wybór tematu ustnego etapu III
												</Typography>
												{sortedSurveySpecializations.length === 0 && (
													<Typography variant='body2'>Brak zdefiniowanych specjalizacji.</Typography>
												)}
												{sortedSurveySpecializations.map((spec: any) => {
													const specTopics = sortedSurveyTopics.filter(
														(t: any) =>
															t.is_linguistic != 1 && (t.specializations || []).some((s: any) => s.id === spec.id),
													);
													return (
														<Grid
															key={spec.id}
															item
															container
															flexDirection={'column'}
															sx={{ mt: 1 }}
														>
															<Divider sx={{ mb: 1 }} />
															<Box
																sx={{
																	display: 'flex',
																	alignItems: 'center',
																	justifyContent: 'space-between',
																	gap: 2,
																}}
															>
																<Typography
																	variant='body1'
																	sx={{ fontWeight: 600 }}
																>
																	{spec.name}
																</Typography>
																<Button
																	variant='text'
																	size='small'
																	onClick={() => {
																		setActiveOralSpecialization(spec);
																		openTopicsDialog('oral');
																	}}
																	sx={{ textTransform: 'none' }}
																>
																	Edytuj
																</Button>
															</Box>
															{specTopics.length === 0 ? (
																<Typography variant='body2'>Brak tematów.</Typography>
															) : (
																<Box
																	component='ul'
																	sx={{
																		px: 0,
																		py: 0,
																		mt: 0.5,
																		mb: 0,
																		listStylePosition: 'inside',
																	}}
																>
																	{specTopics.map((topic: any) => (
																		<Box
																			key={`spec-${spec.id}-topic-${topic.id || topic.topic}`}
																			component='li'
																			sx={{ py: 0.25, display: 'list-item' }}
																		>
																			<span>
																				{topic.category || 'Pozostałe'} — {topic.topic}
																			</span>
																		</Box>
																	))}
																</Box>
															)}
														</Grid>
													);
												})}
											</Grid>
											<Divider sx={{ mt: 1 }} />
											<Grid
												item
												container
												flexDirection={'column'}
											>
												<Box
													sx={{
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'space-between',
														gap: 2,
													}}
												>
													<Typography
														variant='subtitle1'
														sx={{ fontWeight: 600 }}
													>
														Językoznawstwo
													</Typography>
													<Button
														variant='text'
														size='small'
														onClick={() => openTopicsDialog('linguistic')}
														sx={{ textTransform: 'none' }}
													>
														Edytuj
													</Button>
												</Box>
												{sortedSurveyTopics.filter((t: any) => t.is_linguistic == 1).length === 0 ? (
													<Typography variant='body2'>Brak zdefiniowanych tematów językoznawczych.</Typography>
												) : (
													<Box
														component='ul'
														sx={{
															px: 0,
															py: 0,
															mt: 0.5,
															mb: 0,
															listStylePosition: 'inside',
														}}
													>
														{sortedSurveyTopics
															.filter((t: any) => t.is_linguistic == 1)
															.map((topic: any) => (
																<Box
																	key={`linguistic-${topic.id || topic.topic}`}
																	component='li'
																	sx={{ py: 0.25, display: 'list-item' }}
																>
																	<span>{topic.topic}</span>
																</Box>
															))}
													</Box>
												)}
											</Grid>
											<Divider sx={{ mt: 1 }} />
											<Grid
												item
												container
												flexDirection={'column'}
											>
												<Box
													sx={{
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'space-between',
														gap: 2,
													}}
												>
													<Typography
														variant='subtitle1'
														sx={{ fontWeight: 600 }}
													>
														Deklaracje (nocleg)
													</Typography>
													<Button
														variant='text'
														size='small'
														onClick={openDeclarationsDialog}
														sx={{ textTransform: 'none' }}
													>
														Edytuj
													</Button>
												</Box>
												{(surveyDeclarations || []).length === 0 ? (
													<Typography variant='body2'>Brak zdefiniowanych deklaracji.</Typography>
												) : (
													<List
														dense
														sx={{ pt: 1 }}
													>
														{(surveyDeclarations || []).map((declaration: any) => (
															<ListItem
																key={declaration.id || declaration.short_title}
																disableGutters
																sx={{ py: 0.5 }}
															>
																<Typography
																	variant='body2'
																	component='div'
																	dangerouslySetInnerHTML={{
																		__html: cleanUpHtml((declaration.required ? '* ' : '') + declaration.declaration),
																	}}
																/>
															</ListItem>
														))}
													</List>
												)}
											</Grid>
										</Grid>
									)}
								</AccordionDetails>
							</Accordion>
							<Accordion
								expanded={isSurveyStatsOpen}
								onChange={handleSurveyStatsChange}
								sx={{
									mt: 2,
									backgroundColor: 'transparent',
									boxShadow: 'none',
									border: '1px solid',
									borderColor: isHighContrast ? palette.primary.main : 'divider',
								}}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									sx={{ px: 2 }}
								>
									<Typography
										variant='subtitle1'
										color={palette.primary.main}
									>
										Statystyka wypełnienia ankiety
									</Typography>
								</AccordionSummary>
								<AccordionDetails sx={{ px: 2, pb: 2 }}>
									{surveyStatsLoading && <CircularProgress size={24} />}
									{!surveyStatsLoading && !surveyStats && (
										<Typography variant='body2'>
											Nie udało się wczytać statystyk. Zwiń i rozwiń, aby spróbować ponownie.
										</Typography>
									)}
									{!surveyStatsLoading && surveyStats && (
										<Grid
											item
											container
											flexDirection={'column'}
											gap={1}
										>
											<Typography variant='body2'>
												Zakwalifikowani do etapu III: {surveyStats.stage3?.qualified ?? 0}
											</Typography>
											<Typography
												variant='caption'
												color='text.secondary'
											>
												Uczniowie z kwalifikacją do etapu III po etapie IIB.
											</Typography>
											<Typography variant='body2'>
												Dane do zaświadczenia: {surveyStats.stage3?.certificate_filled ?? 0} (
												{surveyStats.stage3?.certificate_percent ?? 0}%)
											</Typography>
											<Typography
												variant='caption'
												color='text.secondary'
											>
												Komplet danych do zaświadczenia (email, adres, data i miejsce urodzenia) dla zakwalifikowanych
												do etapu III.
											</Typography>
											<Divider sx={{ my: 1 }} />
											<Typography variant='body2'>
												Zakwalifikowani do etapu IIIB: {surveyStats.stage3b?.qualified ?? 0}
											</Typography>
											<Typography
												variant='caption'
												color='text.secondary'
											>
												Uczniowie z kwalifikacją do części ustnej etapu III.
											</Typography>
											<Typography variant='body2'>
												Wypełnione ankiety: {surveyStats.stage3b?.questionnaire_filled ?? 0} (
												{surveyStats.stage3b?.questionnaire_percent ?? 0}%)
											</Typography>
											<Typography
												variant='caption'
												color='text.secondary'
											>
												Komplet tematów ustnych i wybranych deklaracji dla zakwalifikowanych do etapu IIIB.
											</Typography>
											<Divider sx={{ my: 1 }} />
											<Typography
												variant='subtitle1'
												sx={{ fontWeight: 600 }}
											>
												Deklaracje
											</Typography>
											{(surveyStats.stage3b?.declarations || []).length === 0 ? (
												<Typography variant='body2'>Brak danych deklaracji.</Typography>
											) : (
												<List
													dense
													sx={{ pt: 1 }}
												>
													{surveyStats.stage3b.declarations.map((item: any) => (
														<ListItem
															key={item.id}
															disableGutters
															sx={{ py: 0.5 }}
														>
															<Typography variant='body2'>
																{item.short_title || 'Bez tytułu'}: {item.total}
															</Typography>
														</ListItem>
													))}
												</List>
											)}
										</Grid>
									)}
								</AccordionDetails>
							</Accordion>
						</Grid>
					</Grid>
				</Grid>
			</Box>
		);
	};

	return (
		<>
			{!olympics ? <Loader /> : renderSettings()}
			<NewOlympicsDialog
				open={openNewOlympicsDialog}
				close={() => setOpenNewOlympicsDialog(false)}
				onSuccess={() => {
					refresh();
					refreshTables();
				}}
			/>
			<TopicsEditorDialog
				open={isOralTopicsDialogOpen}
				type='oral'
				title='Edycja tematów ustnych etapu III'
				topics={surveyTopics}
				specializationId={activeOralSpecialization?.id}
				specializationName={activeOralSpecialization?.name}
				onClose={() => setIsOralTopicsDialogOpen(false)}
				onUpdated={handleTopicsUpdated}
			/>
			<TopicsEditorDialog
				open={isLinguisticTopicsDialogOpen}
				type='linguistic'
				title='Edycja tematów językoznawczych etapu III'
				topics={surveyTopics}
				onClose={() => setIsLinguisticTopicsDialogOpen(false)}
				onUpdated={handleTopicsUpdated}
			/>
			<DeclarationsEditorDialog
				open={isDeclarationsDialogOpen}
				title='Edycja deklaracji'
				declarations={surveyDeclarations}
				onClose={() => setIsDeclarationsDialogOpen(false)}
				onUpdated={handleDeclarationsUpdated}
			/>
			<ConfirmationDialog
				close={() => closeDialog()}
				inProgress={sendingRequest}
				text={dialogContent}
				onSuccess={() => okFunction && okFunction()}
			/>
			<Stage3CodesDialog
				open={isCodesDialogOpen}
				onClose={() => setIsCodesDialogOpen(false)}
				onConfirmDownload={confirmCodesDownload}
				selectedCodesDistrict={selectedCodesDistrict}
				onCodesDistrictChange={handleCodesDistrictChange}
				eligibleCodeDistricts={eligibleCodeDistricts}
				selectedCodesPrintProfile={selectedCodesPrintProfile}
				onCodesPrintProfileChange={handleCodesPrintProfileChange}
				codesPresets={codesPresets}
				codesDefaultPresetSlug={codesDefaultPresetSlug}
				isCodesPresetsLoading={isCodesPresetsLoading}
				codesPresetSlugInput={codesPresetSlugInput}
				onCodesPresetSlugChange={setCodesPresetSlugInput}
				codesPresetLabelInput={codesPresetLabelInput}
				onCodesPresetLabelChange={setCodesPresetLabelInput}
				codesPresetDraftConfig={codesPresetDraftConfig}
				setPresetDraftProperty={(path, value) =>
					setCodesPresetDraftConfig(setProperty(codesPresetDraftConfig, path, value))
				}
				codesSetAsDefaultOnSave={codesSetAsDefaultOnSave}
				onCodesSetAsDefaultOnSaveChange={setCodesSetAsDefaultOnSave}
				isCodesGridTestEnabled={isCodesGridTestEnabled}
				onCodesGridTestEnabledChange={setIsCodesGridTestEnabled}
				isCodesPresetSaving={isCodesPresetSaving}
				onSavePreset={saveCodesPreset}
				onSetSelectedPresetAsDefault={setSelectedCodesPresetAsDefault}
				isCodesDownloading={isCodesDownloading}
				isAttendanceDownloading={isAttendanceDownloading}
				isCodesGenerating={isCodesGenerating}
				hasCodesPresetUnsavedChanges={hasCodesPresetUnsavedChanges}
				codesUnsavedHighlightSx={codesUnsavedHighlightSx}
			/>
			<Stage3AttendanceDialog
				open={isAttendanceDialogOpen}
				onClose={() => setIsAttendanceDialogOpen(false)}
				onConfirmDownload={confirmAttendanceDownload}
				selectedAttendanceDistrict={selectedAttendanceDistrict}
				onAttendanceDistrictChange={handleAttendanceDistrictChange}
				eligibleCodeDistricts={eligibleCodeDistricts}
				attendanceLayoutConfig={attendanceLayoutConfig}
				onAttendanceLayoutConfigChange={setAttendanceLayoutConfig}
				isCodesDownloading={isCodesDownloading}
				isAttendanceDownloading={isAttendanceDownloading}
				isCodesGenerating={isCodesGenerating}
			/>
			<Stage3JuryPairsDialog
				open={isJuryPairsDialogOpen}
				onClose={() => setIsJuryPairsDialogOpen(false)}
				token={token}
				districts={districts}
				onMessage={setMsg}
				isBusyExternal={false}
			/>
			<Stage3DocumentAccessDialog
				open={isDocumentAccessDialogOpen}
				onClose={() => setIsDocumentAccessDialogOpen(false)}
				token={token}
				onMessage={(msg) => setMsg({ msg, severity: 'info' })}
			/>
			<SchoolsRspoRefreshDialog
				open={openRspoRefreshDialog}
				onClose={() => setOpenRspoRefreshDialog(false)}
			/>
		</>
	);
};

export default SettingsPage;
